/*
 * Copyright © 2020-2022 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: 成员实时位置地图容器
 * @LastEditors: yingongjing
 * @LastEditTime: 2025-09-22 19:54:00
 */

import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle
} from 'react'
import './index.scss'
import AMapLoader from '@amap/amap-jsapi-loader'
import CONFIG from '../../../../constants/config'
import { useAppDispatch, useAppSelector } from '../../../../app/hooks'
import actionTypes from '../../../../constants/actionTypes'
import { MemberInfo, RealtimeLocationResp } from '../../index'

// 全局类型已在 src/types/amap.d.ts 中声明

interface IProps {
  locationData: RealtimeLocationResp
  handleClickMarker(memberData: MemberInfo): void
}

// MapContainer组件暴露的方法接口
export interface MapContainerRef {
  centerToMember: (member: MemberInfo) => void
  fitViewToAllMembers: () => void
  setZoom: (zoom: number) => void
}

type MapStatusInfo = {
  lng: number | string
  lat: number | string
  zoom: number
}

const MapContainer = forwardRef<MapContainerRef, IProps>(
  ({ locationData, handleClickMarker }, ref) => {
    const map = useRef<any>()
    const markersRef = useRef<any[]>([])
    const groupState = useAppSelector((state) => state.group)
    const dispatch = useAppDispatch()
    const [curMapStatusInfo, setCurMapStatusInfo] = useState<MapStatusInfo>({
      lng: 0,
      lat: 0,
      zoom: 12
    })

    // 获取成员标记内容
    const getMemberMarkerContent = useCallback((member: MemberInfo) => {
      const {
        userName,
        headimgurl,
        active,
        activeInfo,
        departmentName,
        lng,
        lat
      } = member
      const statusClass = active ? 'active' : 'inactive'
      const statusText = activeInfo || (active ? '在线' : '离线')

      return `<div class='member-marker-wrapper'>
              <!-- 气泡信息（在头像上方） -->
              <div class='member-info-bubble'>
                <div class='member-name'>${userName}</div>
                <div class='member-status ${statusClass}'>
                  <span class='status-indicator'></span>
                  ${statusText}
                </div>
                <!-- 气泡箭头指向头像 -->
                <div class='bubble-arrow'></div>
              </div>
              
              <!-- 头像容器（作为坐标点主体） -->
              <div class='member-avatar-container'>
                <div class='member-avatar'>
                  <img src="${headimgurl}" alt="${userName}" onerror="this.src='data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMjAiIGN5PSIyMCIgcj0iMjAiIGZpbGw9IiNGNUY1RjUiLz4KPHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTEyIDEyQzE0LjIwOTEgMTIgMTYgMTAuMjA5MSAxNiA4QzE2IDUuNzkwODYgMTQuMjA5MSA0IDEyIDRDOS43OTA4NiA0IDggNS43OTA4NiA4IDhDOCAxMC4yMDkxIDkuNzkwODYgMTIgMTIgMTJaIiBmaWxsPSIjOTk5OTk5Ii8+CjxwYXRoIGQ9Ik0xMiAxNEM5LjMzIDEzLjk5IDcuMDEgMTUuNjIgNiAxOEMxMC4wMSAxOCAxMy45OSAxOCAxOCAxOEMxNi45OSAxNS42MiAxNC42NyAxMy45OSAxMiAxNFoiIGZpbGw9IiM5OTk5OTkiLz4KPHN2Zz4KPC9zdmc+Cg==' />" />
                </div>
              </div>
            </div>`
    }, [])

    // 清除所有标记
    const clearMarkers = useCallback(() => {
      if (markersRef.current.length > 0) {
        markersRef.current.forEach((marker) => {
          if (map.current) {
            map.current.remove(marker)
          }
        })
        markersRef.current = []
      }
    }, [])

    // 添加成员标记
    const addMemberMarkers = useCallback(() => {
      if (!map.current || !locationData.memberInfo?.length) return

      clearMarkers()

      locationData.memberInfo.forEach((member) => {
        const { lng, lat } = member

        if (!lng || !lat) return

        const marker = new window.AMap.Marker({
          position: new window.AMap.LngLat(lng, lat),
          content: getMemberMarkerContent(member),
          anchor: 'center',
          offset: new window.AMap.Pixel(0, -20),
          id: member.userId
        })

        marker.on('click', () => {
          handleClickMarker(member)
        })

        map.current.add(marker)
        markersRef.current.push(marker)
      })

      // 自适应显示所有标记
      if (markersRef.current.length > 0) {
        map.current.setFitView(markersRef.current, false, [20, 20, 20, 20])
      }
    }, [
      locationData.memberInfo,
      getMemberMarkerContent,
      handleClickMarker,
      clearMarkers
    ])

    // 初始化地图
    const initMap = useCallback(() => {
      dispatch({
        type: actionTypes.SET_LOADING,
        data: true
      })

      AMapLoader.load({
        key: CONFIG.GAODE_KEY,
        version: '2.0',
        plugins: ['AMap.Scale', 'AMap.ToolBar']
      })
        .then((AMap) => {
          window.AMap = AMap

          const mapInstance = new AMap.Map('map-container', {
            zoom: curMapStatusInfo.zoom,
            center: [curMapStatusInfo.lng, curMapStatusInfo.lat],
            mapStyle: 'amap://styles/normal',
            viewMode: '2D'
          })

          // 添加地图控件
          mapInstance.addControl(new AMap.Scale())
          mapInstance.addControl(
            new AMap.ToolBar({
              position: {
                bottom: '80px',
                left: '30px'
              }
            })
          )

          map.current = mapInstance
          window.MapInstance = mapInstance

          // 地图移动结束事件
          mapInstance.on('moveend', () => {
            const center = mapInstance.getCenter()
            const zoom = mapInstance.getZoom()
            setCurMapStatusInfo({
              lng: center.lng,
              lat: center.lat,
              zoom
            })
          })

          dispatch({
            type: actionTypes.SET_LOADING,
            data: false
          })
        })
        .catch((error) => {
          console.error('地图初始化失败:', error)
          dispatch({
            type: actionTypes.SET_LOADING,
            data: false
          })
        })
    }, [dispatch, curMapStatusInfo])

    // 组件挂载时初始化地图
    useEffect(() => {
      initMap()

      return () => {
        // 清理地图资源
        if (map.current) {
          clearMarkers()
          map.current.destroy()
        }
      }
    }, [])

    // 当位置数据变化时更新标记
    useEffect(() => {
      if (map.current) {
        addMemberMarkers()
      }
    }, [addMemberMarkers])

    // 暴露给父组件的方法
    useImperativeHandle(
      ref,
      () => ({
        // 将地图中心定位到指定成员
        centerToMember: (member: MemberInfo) => {
          if (map.current && member.lng && member.lat) {
            map.current.setCenter([member.lng, member.lat])
            map.current.setZoom(16) // 放大到合适的级别

            // 将该成员的标记置于最上层显示
            markersRef.current.forEach((marker, index) => {
              const markerPosition = marker.getPosition()
              const markerContent = marker.getContent()

              if (
                markerPosition &&
                Math.abs(markerPosition.lng - member.lng) < 0.000001 &&
                Math.abs(markerPosition.lat - member.lat) < 0.000001 &&
                markerContent &&
                markerContent.includes(member.userName)
              ) {
                // 找到对应的标记，设置为最高层级
                marker.setzIndex(999)
              } else {
                // 其他标记设置为普通层级
                marker.setzIndex(1)
              }
            })
          }
        },
        // 自适应显示所有成员
        fitViewToAllMembers: () => {
          if (map.current && markersRef.current.length > 0) {
            map.current.setFitView(markersRef.current, false, [20, 20, 20, 20])
          }
        },
        // 设置地图缩放级别
        setZoom: (zoom: number) => {
          if (map.current) {
            map.current.setZoom(zoom)
          }
        }
      }),
      []
    )

    return (
      <div className="map-container-wrapper">
        <div id="map-container" className="map-container"></div>
        {locationData.memberInfo?.length === 0 && (
          <div className="no-data-overlay">
            <div className="no-data-content">
              <div className="no-data-icon">📍</div>
              <div className="no-data-text">暂无成员位置信息</div>
            </div>
          </div>
        )}
      </div>
    )
  }
)

export default MapContainer
